<script>
    import { onMount } from "svelte";
    import { getDatabaseTables, getTableData, dialogType, dialogMessage, showDialog } from "../http.js";
    import Loader from "./Loader.svelte";

    let tables = [];
    let selectedTable = null;
    let tableData = null;
    let toggleLoader = false;
    let currentPage = 1;
    let pageSize = 50;
    let selectedMonth = null;
    let selectedYear = null;
    const tableSelectId = 'data-viewer-table-select';
    const filterMonthSelectId = 'data-viewer-month-filter';
    const filterYearSelectId = 'data-viewer-year-filter';

    const months = [
        { value: null, label: "Wszystkie" },
        { value: 1, label: "Styczeń" },
        { value: 2, label: "Luty" },
        { value: 3, label: "Marzec" },
        { value: 4, label: "Kwiecień" },
        { value: 5, label: "Maj" },
        { value: 6, label: "Czerwiec" },
        { value: 7, label: "Lipiec" },
        { value: 8, label: "Sierpień" },
        { value: 9, label: "Wrzesień" },
        { value: 10, label: "Październik" },
        { value: 11, label: "Listopad" },
        { value: 12, label: "Grudzień" }
    ];

    const years = [
        { value: null, label: "Wszystkie" },
        ...Array.from({ length: 5 }, (_, i) => {
            const year = new Date().getFullYear() - i;
            return { value: year, label: String(year) };
        })
    ];

    const loadTables = async () => {
        toggleLoader = true;
        try {
            tables = await getDatabaseTables();
            if (tables.length > 0) {
                selectedTable = tables[0].name;
                await loadTableData();
            }
        } catch (error) {
            showDialog.update(() => true);
            dialogType.update(() => "is-danger");
            dialogMessage.update(() => "Nie możemy pobrać listy tabel.");
        } finally {
            toggleLoader = false;
        }
    };

    const loadTableData = async () => {
        if (!selectedTable) return;
        
        toggleLoader = true;
        try {
            const offset = (currentPage - 1) * pageSize;
            tableData = await getTableData(selectedTable, pageSize, offset, selectedMonth, selectedYear);
        } catch (error) {
            showDialog.update(() => true);
            dialogType.update(() => "is-danger");
            dialogMessage.update(() => "Nie możemy pobrać danych z tabeli.");
        } finally {
            toggleLoader = false;
        }
    };

    const handleTableChange = () => {
        currentPage = 1;
        loadTableData();
    };

    const handleFilterChange = () => {
        currentPage = 1;
        loadTableData();
    };

    const nextPage = () => {
        const totalPages = Math.ceil(tableData.total_count / pageSize);
        if (currentPage < totalPages) {
            currentPage++;
            loadTableData();
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            currentPage--;
            loadTableData();
        }
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) {
            return '-';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };

    const getColumns = (data) => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0]);
    };

    onMount(async () => {
        await loadTables();
    });

    $: totalPages = tableData ? Math.ceil(tableData.total_count / pageSize) : 0;
</script>

<section class="view">
    <Loader state={toggleLoader}/>

    <div class="filter-bar">
        <label class="filter-bar__label" for={tableSelectId}>Tabela</label>
        <div class="select is-wide">
            <select id={tableSelectId} bind:value={selectedTable} on:input={handleTableChange}>
                {#each tables as table}
                    <option value={table.name}>{table.name} ({table.database}) - {table.description}</option>
                {/each}
            </select>
        </div>

        <label class="filter-bar__label" for={filterMonthSelectId}>Miesiąc</label>
        <div class="select">
            <select id={filterMonthSelectId} bind:value={selectedMonth} on:input={handleFilterChange}>
                {#each months as month}
                    <option value={month.value}>{month.label}</option>
                {/each}
            </select>
        </div>

        <label class="filter-bar__label" for={filterYearSelectId}>Rok</label>
        <div class="select">
            <select id={filterYearSelectId} bind:value={selectedYear} on:input={handleFilterChange}>
                {#each years as year}
                    <option value={year.value}>{year.label}</option>
                {/each}
            </select>
        </div>

        {#if tableData}
            <span class="filter-bar__note">{tableData.total_count} wierszy</span>
        {/if}
    </div>

    {#if tableData}
        <div class="table-wrap">
            <table class="table table-modern is-fullwidth is-mono">
                <thead>
                    <tr>
                        {#each getColumns(tableData.data) as column}
                            <th>{column}</th>
                        {/each}
                    </tr>
                </thead>
                <tbody>
                    {#if tableData.data.length === 0}
                        <tr>
                            <td colspan={Math.max(getColumns(tableData.data).length, 1)}>
                                <div class="empty-state">
                                    <img src="/logo.png" alt="" width="48" height="48">
                                    <p>Brak danych w tej tabeli.</p>
                                </div>
                            </td>
                        </tr>
                    {/if}
                    {#each tableData.data as row}
                        <tr>
                            {#each getColumns(tableData.data) as column}
                                <td>
                                    {#if typeof row[column] === 'object' && row[column] !== null}
                                        <details>
                                            <summary>JSON</summary>
                                            <pre class="json-content">{formatValue(row[column])}</pre>
                                        </details>
                                    {:else if row[column] === null || row[column] === undefined}
                                        <span class="cell-sub">NULL</span>
                                    {:else}
                                        {formatValue(row[column])}
                                    {/if}
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <div class="pager">
            <button class="button" on:click={prevPage} disabled={currentPage === 1}>Poprzednia</button>
            <span class="cell-sub">{currentPage} / {totalPages}</span>
            <button class="button" on:click={nextPage} disabled={currentPage >= totalPages}>Następna</button>
        </div>
    {/if}
</section>

<style>
    .json-content {
        background-color: var(--muted);
        color: var(--foreground);
        padding: 0.5rem;
        border-radius: calc(var(--radius) - 6px);
        border: 1px solid var(--border);
        font-size: var(--text-xs);
        max-width: 360px;
        max-height: 260px;
        overflow: auto;
        white-space: pre-wrap;
        word-wrap: break-word;
    }

    details summary {
        cursor: pointer;
        color: var(--primary);
        font-weight: 600;
    }
</style>
